import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MEDIA } from "@/lib/media";
import { Reveal } from "@/components/Reveal";
import { MagneticButton } from "@/components/MagneticButton";
import { WaterLens } from "@/components/WaterLens";
import { WaterDropReveal } from "@/components/WaterDropReveal";
import { RailingAssembly } from "@/components/RailingAssembly";
import { RippleDivider } from "@/components/RippleDivider";
import { api, fileUrl } from "@/lib/api";

const Hero = () => (
  <section className="relative h-screen overflow-hidden bg-onyx-black" data-testid="hero">
    <WaterLens image={MEDIA.hero} className="absolute inset-0 w-full h-full" />
    <div className="relative h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24 pointer-events-none">
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 1 }}
        className="text-white/70 text-[11px] tracking-[0.35em] uppercase mb-6">
        Glass · Cable · Metal Railing Systems
      </motion.p>
      <h1 className="text-white font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl tracking-tightest leading-[0.95] hero-text-shadow max-w-5xl">
        {["Architectural Railings.", "Built With Precision."].map((line, i) => (
          <span key={line} className="block overflow-hidden">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.4 + i * 0.15, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}>{line}</motion.span>
          </span>
        ))}
      </h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
        className="text-white/80 text-base md:text-lg max-w-xl mt-8 leading-relaxed">
        Multiline, panel, and picket railing systems engineered for stairs, balconies, and mezzanines in modern commercial and high-end spaces.
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }} className="flex flex-wrap gap-4 mt-10 pointer-events-auto">
        <Link to="/contact"><MagneticButton data-testid="hero-cta-proposal" className="group bg-white text-onyx-black px-8 py-4 text-[12px] tracking-[0.15em] uppercase flex items-center gap-3 hover:bg-onyx-wood transition-colors">Request a Proposal <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></MagneticButton></Link>
        <Link to="/projects"><MagneticButton data-testid="hero-cta-work" className="border border-white/40 text-white px-8 py-4 text-[12px] tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">View Our Work</MagneticButton></Link>
      </motion.div>
    </div>
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-[10px] tracking-[0.3em] uppercase flex flex-col items-center gap-2 pointer-events-none">
      Scroll
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-8 bg-white/40" />
    </div>
  </section>
);

const ExpertiseCard = ({ service, index }) => (
  <Reveal delay={index * 0.08}>
    <Tilt tiltMaxAngleX={6} tiltMaxAngleY={6} glareEnable glareMaxOpacity={0.15} glareColor="#ffffff" glarePosition="all" scale={1.02} transitionSpeed={1200} className="h-full">
      <Link to="/services" data-testid={`expertise-card-${index}`} className="group block relative h-[460px] overflow-hidden bg-onyx-black">
        <img src={fileUrl(service.image)} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-110 group-hover:opacity-100 transition-all duration-[900ms] ease-out" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
        <div className="absolute inset-0 p-8 flex flex-col justify-end">
          <div className="flex items-end justify-between">
            <div>
              <h3 className="text-white font-display font-bold text-3xl tracking-tight leading-tight">{service.title}</h3>
              <p className="text-white/60 text-sm mt-3 max-w-xs opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">{service.short_description}</p>
            </div>
            <div className="w-11 h-11 shrink-0 border border-white/30 flex items-center justify-center group-hover:bg-onyx-wood group-hover:border-onyx-wood transition-colors">
              <ArrowUpRight size={18} className="text-white group-hover:text-onyx-black transition-colors" />
            </div>
          </div>
        </div>
      </Link>
    </Tilt>
  </Reveal>
);

export default function Home() {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.featured).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <Hero />

      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-28 md:py-40">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-3"><span className="text-[11px] tracking-[0.3em] uppercase text-onyx-metal">The Firm</span></div>
          <Reveal className="md:col-span-9">
            <p className="font-display font-semibold text-3xl md:text-5xl tracking-tight leading-[1.15] text-onyx-charcoal">
              We are a specialty railing contractor. Every post, rail, and panel is engineered in-house, fabricated to spec, and installed to fractions of an inch — where safety meets sightline.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Scroll-driven railing installation animation */}
      <RippleDivider top="#F7F6F3" bottom="#0A0A0A" />
      <RailingAssembly />
      <RippleDivider top="#0A0A0A" bottom="#F7F6F3" />

      {/* Expertise — 3 railing systems */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-28 md:py-40" data-testid="expertise-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="text-[11px] tracking-[0.3em] uppercase text-onyx-metal">Our Systems</span>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tightest mt-4 leading-none">Three ways<br />to define an edge.</h2>
          </div>
          <Link to="/services" className="group text-sm tracking-[0.12em] uppercase flex items-center gap-3 text-onyx-charcoal hover:text-onyx-wood transition-colors">All Services <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s, i) => <ExpertiseCard key={s.id} service={s} index={i} />)}
        </div>
      </section>

      {/* Featured projects with water-drop reveal */}
      <RippleDivider top="#F7F6F3" bottom="#1A1A1A" />
      {projects.length > 0 && (
        <section className="bg-onyx-charcoal text-white py-28 md:py-40" data-testid="featured-projects">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="text-[11px] tracking-[0.3em] uppercase text-onyx-wood">Selected Work</span>
                <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tightest mt-4">Recent Installations</h2>
              </div>
              <Link to="/projects" className="group text-sm tracking-[0.12em] uppercase flex items-center gap-3 text-white/70 hover:text-white transition-colors">Full Gallery <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {projects.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <Link to={`/projects/${p.slug}`} className="group block" data-testid={`home-project-${i}`}>
                    <WaterDropReveal src={fileUrl(p.hero_image)} alt={p.name} className="h-[440px]" />
                    <div className="pt-5 flex items-start justify-between">
                      <div>
                        <h3 className="font-display font-bold text-xl tracking-tight">{p.name}</h3>
                        <p className="text-white/50 text-sm mt-1">{p.location} · {p.type}</p>
                      </div>
                      <span className="font-mono text-sm text-onyx-wood">{p.year}</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative py-32 md:py-48 overflow-hidden" data-testid="home-cta">
        <img src={MEDIA.glassBalcony2} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-white font-display font-extrabold text-4xl md:text-7xl tracking-tightest leading-none">Let's build something exceptional.</h2>
            <p className="text-white/70 mt-8 text-lg max-w-xl mx-auto">Share your drawings and scope. Our team will engineer a railing proposal built for precision.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-10">
              <Link to="/contact"><MagneticButton className="bg-white text-onyx-black px-10 py-4 text-[12px] tracking-[0.15em] uppercase hover:bg-onyx-wood transition-colors">Request a Proposal</MagneticButton></Link>
              <Link to="/configurator"><MagneticButton data-testid="cta-estimate" className="border border-white/40 text-white px-10 py-4 text-[12px] tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">Get an Instant Estimate</MagneticButton></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
